import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ClipboardCheck, Flag, Shield, ShieldCheck } from 'lucide-react';

/**
 * The admin's tools in ONE header button (issue 1069). Three separate icons
 * pushed a signed-in admin's header past 1280 px wide (e2e/site-widths.spec.js);
 * one "Admin" menu keeps every destination at one button's width.
 *
 * A menu button in the WAI-ARIA sense: aria-haspopup="menu" and aria-expanded on
 * the button; role="menu" with role="menuitem" links. Opening by keyboard puts
 * focus on the first item; ArrowDown/ArrowUp move between items, Home/End jump;
 * Escape closes and returns focus to the button; Tab, a click outside, or
 * choosing an item closes it.
 */
export const ADMIN_LINKS = [
  { name: 'Reports', title: 'Problem reports', href: '/admin/reports', icon: Flag },
  { name: 'User content', title: 'User content moderation', href: '/admin/content', icon: ShieldCheck },
  { name: 'Verification', title: 'Verification coverage', href: '/admin/verification', icon: ClipboardCheck },
];

export default function AdminMenu({ isActive }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const button = useRef(null);
  // The items, read from the menu itself (a ref on a router Link is not guaranteed to reach the anchor).
  const menuItems = () => Array.from(wrap.current?.querySelectorAll('[role="menuitem"]') ?? []);
  const focusOnOpen = useRef(false);
  const anyActive = ADMIN_LINKS.some((l) => isActive(l.href));

  // Outside click closes it.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Opened from the keyboard: focus moves into the menu.
  useEffect(() => {
    if (open && focusOnOpen.current) { focusOnOpen.current = false; menuItems()[0]?.focus(); }
  }, [open]);

  const close = (refocus) => { setOpen(false); if (refocus) button.current?.focus(); };

  const onButtonKey = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      focusOnOpen.current = true;
      setOpen(true);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      close(true);
    }
  };

  const onMenuKey = (e) => {
    const list = menuItems();
    const at = list.indexOf(document.activeElement);
    if (e.key === 'Escape') { e.preventDefault(); close(true); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); list[(at + 1) % list.length]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); list[(at - 1 + list.length) % list.length]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); list[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); list[list.length - 1]?.focus(); }
    else if (e.key === 'Tab') setOpen(false);
  };

  return (
    <div className="dropdown" ref={wrap}>
      <button
        ref={button}
        type="button"
        className={`nav-link ${anyActive ? 'nav-link-active' : 'nav-link-inactive'}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="admin-menu"
        aria-label="Admin"
        title="Admin tools"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onButtonKey}
      >
        <Shield className="w-4 h-4" />
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div id="admin-menu" role="menu" aria-label="Admin" className="dropdown-menu" style={{ left: 'auto', right: 0 }} onKeyDown={onMenuKey}>
          {ADMIN_LINKS.map((l) => {
            const Icon = l.icon;
            const active = isActive(l.href);
            return (
              <Link
                key={l.href}
                to={l.href}
                role="menuitem"
                tabIndex={-1}
                title={l.title}
                className={`dropdown-item ${active ? 'dropdown-item-active' : 'dropdown-item-inactive'}`}
                onClick={() => setOpen(false)}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span>{l.name}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
